import theme from './utils/maps/themes/red_hat_antwerp';

/**
 * Custom Google Maps
 * 
 * @example
 * <!-- HTML markup -->
 * <div class="map">
 *   <div data-map data-lat="46.2392" data-lng="20.11639"></div>
 * </div>
 * 
 * // init single map:
 * new GoogleMap(document.querySelector('[data-map]'));
 * 
 * // init multiple maps:
 * Array.from(document.querySelectorAll('[data-map]')).forEach(mapEl => new GoogleMap(mapEl));
 * 
 * // init with custom options
 * new GoogleMap(document.querySelector('[data-map]'), {
 *   // custom options...
 * });
 */
class GoogleMap {
  /**
   * GoogleMap constructor
   * 
   * @param {HTMLElement|string} mapEl - Map HTML element or CSS selector
   * @param {object} options - Optional settings
   * @param {number} [options.zoom=12] - Map zoom
   * @param {boolean} [options.marker=true] - Show marker?
   * @param {boolean} [options.mapTypeControl=false] - Show map type controls?
   * @param {boolean} [options.zoomControl=true] - Show map zoom controls?
   * @param {boolean} [options.scaleControl=false] - Show map scale controls?
   * @param {string} [options.markerPath] - Custom marker SVG path data
   * @param {string} [options.markerFill=#c8102e] - Custom marker SVG path fill color
   * @param {number} [options.markerOpacity=1] - Custom marker SVG path fill opacity (0-1)
   * @param {number} [options.markerWidth=16] - Custom marker SVG width
   * @param {number} [options.markerHeight=16] - Custom marker SVG height
   * @param {number} [options.markerScale=3] - Custom marker SVG scale
   */
  constructor(mapEl, options = {}) {
    const _defaults = {
      zoom: 12,
      marker: true,
      mapTypeControl: false,
      zoomControl: true,
      scaleControl: false,
      markerPath: 'M8 1.2c-1.4 0-2.7.6-3.7 1.5S2.8 5.1 2.8 6.5c0 4.2 5.2 8.2 5.2 8.2s5.2-4 5.2-8.2c0-1.4-.6-2.7-1.5-3.7S9.4 1.2 8 1.2zM8 9c-.5 0-1-.1-1.4-.4-.4-.3-.7-.7-.9-1.1-.2-.5-.2-1-.2-1.5.1-.5.3-.9.7-1.3.4-.3.8-.6 1.3-.7.5 0 1 0 1.5.2s.8.5 1.1.9c.3.4.4.9.4 1.4 0 .7-.3 1.3-.7 1.8-.5.4-1.1.7-1.8.7z',
      markerFill: '#c8102e',
      markerOpacity: 1,
      markerWidth: 16,
      markerHeight: 16,
      markerScale: 3,
    };
    this.opts = { ..._defaults, ...options };

    this.mapEl = (typeof mapEl === 'string') ? document.querySelector(mapEl) : mapEl;
    this.map = null;
    this.marker = null;
    this.mapCenter = {
      lat: (this.mapEl.hasAttribute('data-lat')) ? parseFloat(this.mapEl.getAttribute('data-lat')) : 46.2392,
      lng: (this.mapEl.hasAttribute('data-lng')) ? parseFloat(this.mapEl.getAttribute('data-lng')) : 20.11639,
    };

    this.init();
  }

  init() {
    if (!this.map) {
      this.map = new google.maps.Map(this.mapEl, {
        zoom: this.opts.zoom,
        center: this.mapCenter,
        disableDefaultUI: true,
        mapTypeControl: this.opts.mapTypeControl,
        zoomControl: this.opts.zoomControl,
        scaleControl: this.opts.scaleControl,
        styles: theme,
      });
      this.addMarker();
    }
  }

  addMarker() {
    if (!this.marker && this.map && this.opts.marker) {
      this.marker = new google.maps.Marker({
        position: this.mapCenter,
        map: this.map,
        icon: {
          path: this.opts.markerPath,
          fillColor: this.opts.markerFill,
          fillOpacity: this.opts.markerOpacity,
          anchor: new google.maps.Point(this.opts.markerWidth / 2, this.opts.markerHeight),
          strokeWeight: 0,
          scale: this.opts.markerScale,
        },
      });
    }
  }
}

export default GoogleMap;
